import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Clock, AlertTriangle, Users, Target, UserPlus, CalendarClock } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { fmtDateTime, fmtDate, fmtEUR, label } from "../lib/format";

function Block({ title, icon: Icon, accent, children, count }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 flex flex-col">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}15` }}><Icon className="w-4 h-4" style={{ color: accent }} /></div>
        <p className="text-sm font-semibold font-heading text-slate-700">{title}</p>
        <span className="ml-auto text-xs font-mono text-slate-400">{count}</span>
      </div>
      <div className="p-2 flex-1 overflow-y-auto max-h-80 kanban-scroll">{children}</div>
    </div>
  );
}

export default function CommandCenter() {
  const nav = useNavigate();
  const [acts, setActs] = useState([]);
  const [opps, setOpps] = useState([]);
  const [leads, setLeads] = useState([]);

  const load = useCallback(async () => {
    const [a, o, l] = await Promise.all([
      api.get("/activities"), api.get("/opportunities", { params: { status: "open" } }), api.get("/leads"),
    ]);
    setActs(a.data); setOpps(o.data); setLeads(l.data);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm-refresh", h);
    const t = setInterval(load, 60000);
    return () => { window.removeEventListener("crm-refresh", h); clearInterval(t); };
  }, [load]);

  const complete = async (id) => { await api.put(`/activities/${id}`, { status: "completata" }); toast.success("Completata"); load(); };
  const reschedule = async (id) => {
    const tomorrow = new Date(Date.now() + 86400000).toISOString();
    await api.put(`/activities/${id}`, { due_date: tomorrow, status: "da_fare" });
    toast.success("Riprogrammata a domani"); load();
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dueDate = (a) => (a.due_date ? new Date(a.due_date) : null);
  const notDone = (a) => a.status !== "completata";

  const todayActs = acts.filter((a) => notDone(a) && dueDate(a) && dueDate(a).toDateString() === new Date().toDateString());
  const overdue = acts.filter((a) => notDone(a) && dueDate(a) && dueDate(a) < today);
  const meetings = todayActs.filter((a) => ["meeting", "video call"].includes(a.type));

  // opportunities without future activity
  const oppFuture = new Set(acts.filter((a) => notDone(a) && a.opportunity_id && dueDate(a) && dueDate(a) >= today).map((a) => a.opportunity_id));
  const staleOpps = opps.filter((o) => !oppFuture.has(o.id));
  // leads without future activity
  const leadFuture = new Set(acts.filter((a) => notDone(a) && a.lead_id && dueDate(a) && dueDate(a) >= today).map((a) => a.lead_id));
  const staleLeads = leads.filter((l) => !leadFuture.has(l.id) && !["convertito", "non_qualificato"].includes(l.status));

  const ActRow = ({ a }) => (
    <div data-testid={`cc-activity-${a.id}`} className="flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50 group">
      <button data-testid={`cc-complete-${a.id}`} onClick={() => complete(a.id)} className="icon-btn shrink-0"><Circle className="w-4 h-4 text-slate-300 hover:text-teal-500" /></button>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 truncate flex items-center gap-2">{a.subject}{a.is_overdue && <span data-testid={`cc-overdue-${a.id}`} className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-50 text-red-700 border border-red-200">SCADUTA{a.overdue_notified_at && " · notificata"}</span>}</p>
        <p className="text-xs text-slate-400 truncate">{label("activity_type", a.type)} · {a.company?.legal_name || "—"} · {fmtDateTime(a.due_date)}</p>
      </div>
      <button data-testid={`cc-reschedule-${a.id}`} onClick={() => reschedule(a.id)} className="opacity-0 group-hover:opacity-100 touch-visible text-xs text-teal-600 hover:underline shrink-0 min-h-[32px] px-1">+1g</button>
    </div>
  );

  return (
    <div>
      <PageHeader title="Command Center · TODAY" subtitle={`${new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} · il centro del lavoro quotidiano`} />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <Block title="Attività di oggi" icon={CalendarClock} accent="#0D9488" count={todayActs.length}>
          {todayActs.length ? todayActs.map((a) => <ActRow key={a.id} a={a} />) : <Empty text="Nessuna attività oggi" />}
        </Block>
        <Block title="Attività scadute" icon={AlertTriangle} accent="#dc2626" count={overdue.length}>
          {overdue.length ? overdue.map((a) => <ActRow key={a.id} a={a} />) : <Empty text="Nessuna attività scaduta" />}
        </Block>
        <Block title="Meeting di oggi" icon={Users} accent="#6D28D9" count={meetings.length}>
          {meetings.length ? meetings.map((a) => <ActRow key={a.id} a={a} />) : <Empty text="Nessun meeting oggi" />}
        </Block>
        <Block title="Opportunità da aggiornare" icon={Target} accent="#1D4ED8" count={staleOpps.length}>
          {staleOpps.length ? staleOpps.map((o) => (
            <button key={o.id} data-testid={`cc-opp-${o.id}`} onClick={() => nav("/opportunita")} className="w-full text-left flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50">
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{o.name}</p><p className="text-xs text-slate-400 truncate">{o.company?.legal_name || "—"}</p></div>
              <span className="text-xs font-mono text-slate-500 shrink-0">{fmtEUR(o.value)}</span>
            </button>
          )) : <Empty text="Tutte aggiornate" />}
        </Block>
        <Block title="Lead senza attività" icon={UserPlus} accent="#B45309" count={staleLeads.length}>
          {staleLeads.length ? staleLeads.map((l) => (
            <button key={l.id} data-testid={`cc-lead-${l.id}`} onClick={() => nav("/lead")} className="w-full text-left flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-50">
              <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-800 truncate">{l.title}</p><p className="text-xs text-slate-400 truncate">{l.company_name || "—"}</p></div>
              <span className="text-xs font-mono text-teal-600 shrink-0">{l.score}</span>
            </button>
          )) : <Empty text="Nessun lead scoperto" />}
        </Block>
        <Block title="Prospect da ricontattare" icon={Clock} accent="#64748b" count={staleLeads.slice(0, 5).length}>
          {staleLeads.slice(0, 5).length ? staleLeads.slice(0, 5).map((l) => (
            <div key={l.id} className="px-2 py-2 text-sm text-slate-700">{l.company_name || l.title}</div>
          )) : <Empty text="Nessun prospect" />}
        </Block>
      </div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="flex items-center gap-2 px-2 py-6 text-sm text-slate-400 justify-center"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {text}</div>;
}
